import { useMemo, useState } from "react";
import {
  Card,
  Title,
  Subtitle,
  BarChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TabGroup,
  TabList,
  Tab,
  Badge,
} from "@tremor/react";
import { Users, Info, MousePointerClick } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TableScroll } from "@/components/ui/TableScroll";
import { deriveProfitabilitate } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { useDrilldown } from "@/components/drilldown/Drilldown";
import { labelCategorie } from "@/lib/labels";
import { eur, eurAxis, procent } from "@/lib/format";

export function ProfitabilitateSection() {
  useFilterVersion();
  const [tab, setTab] = useState(0);
  const dimensiune = tab === 0 ? "client" : "categorie_produs";
  const { openDrill } = useDrilldown();

  const randuri = useMemo(
    () =>
      deriveProfitabilitate(dimensiune)
        .map((r) => ({
          ...r,
          eticheta: dimensiune === "categorie_produs" ? labelCategorie(r.valoare) : r.valoare,
        }))
        .sort((a, b) => b.marja_eur - a.marja_eur),
    [dimensiune],
  );

  const chartData = randuri.map((r) => ({ eticheta: r.eticheta, valoare: r.valoare, Marjă: r.marja_eur }));

  function drillRand(valoare: string) {
    openDrill({ type: dimensiune === "client" ? "client" : "categorie", valoare });
  }

  function drillChart(v: unknown) {
    const valoare = (v as { valoare?: string } | null)?.valoare;
    if (valoare) drillRand(valoare);
  }

  return (
    <section>
      <SectionHeader
        id="profitabilitate"
        icon={Users}
        titlu="3. Profitabilitate per client / categorie produs"
        descriere='Grain: comandă. Răspunde "unde punem efortul de vânzare?" și "cât de jos putem coborî prețul?"'
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <TabGroup index={tab} onIndexChange={setTab}>
          <TabList variant="solid">
            <Tab>Per client</Tab>
            <Tab>Per categorie produs</Tab>
          </TabList>
        </TabGroup>
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <MousePointerClick className="h-3.5 w-3.5" />
          Click pe o bară / rând → comenzile și marja lui.
        </span>
      </div>

      <Card className="ring-slate-200">
        <Title>Marjă (€) — {dimensiune === "client" ? "top clienți" : "categorii produs"}</Title>
        <Subtitle>Sortat descrescător după marja generată · pe perioada filtrată</Subtitle>
        <BarChart
          className="mt-4 h-80 w-full"
          data={chartData}
          index="eticheta"
          categories={["Marjă"]}
          colors={["emerald"]}
          valueFormatter={eurAxis}
          layout="vertical"
          showLegend={false}
          yAxisWidth={dimensiune === "client" ? 132 : 96}
          noDataText="Fără date pe filtrul curent"
          onValueChange={drillChart}
        />
      </Card>

      <Card className="mt-4 ring-slate-200">
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Prag preț minim</strong> = cost_total / venit — sub acest procent din prețul
            actual, comanda devine pierdere. Folosește-l ca reper de negociere, nu ca țintă.
          </span>
        </div>
        <TableScroll minWidth="52rem">
          <Table>
            <TableHead>
              <TableRow className="border-slate-200">
                <TableHeaderCell>{dimensiune === "client" ? "Client" : "Categorie"}</TableHeaderCell>
                <TableHeaderCell className="text-right">Nr. comenzi</TableHeaderCell>
                <TableHeaderCell className="text-right">Venit</TableHeaderCell>
                <TableHeaderCell className="text-right">Cost</TableHeaderCell>
                <TableHeaderCell className="text-right">Marjă</TableHeaderCell>
                <TableHeaderCell className="text-right">Marjă %</TableHeaderCell>
                <TableHeaderCell className="text-right">Prag preț min.</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {randuri.map((r) => (
                <TableRow
                  key={r.valoare}
                  onClick={() => drillRand(r.valoare)}
                  className="cursor-pointer border-slate-100 transition-colors hover:bg-brand-50/60"
                >
                  <TableCell className="font-medium text-slate-800">{r.eticheta}</TableCell>
                  <TableCell className="text-right tabular">{r.nr_comenzi}</TableCell>
                  <TableCell className="text-right tabular text-slate-600">{eur(r.venit_eur, 0)}</TableCell>
                  <TableCell className="text-right tabular text-slate-600">{eur(r.cost_eur, 0)}</TableCell>
                  <TableCell className="text-right tabular font-medium text-slate-800">{eur(r.marja_eur, 0)}</TableCell>
                  <TableCell className="text-right">
                    <Badge size="xs" color={r.marja_pct >= 25 ? "emerald" : r.marja_pct >= 10 ? "amber" : "rose"}>
                      {procent(r.marja_pct)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular text-slate-500">{procent(r.prag_pret_min_pct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>
    </section>
  );
}
